import { SvgCheckboxChecked } from '@chilipiper/icons/src/CheckboxChecked'
import { SvgCheckboxSemi } from '@chilipiper/icons/src/CheckboxSemi'
import { SvgCheckboxRounded } from '@chilipiper/icons/src/design-system/CheckboxRounded'
import React, { FC } from 'react'

import { Field as FinalField } from 'react-final-form'

import styled from 'styled-components'
import { Box, Flex } from '../../new/core-components/box'
import { Icon } from '../../new/core-components/icon'
import { Text } from '../../new/core-components/text'
import { SpaceProps } from '../../new/theme/spacing'

type CheckboxValue = boolean | '-'

/** @deprecated See {@link Checkbox} */
export type CheckboxProps = {
  'aria-label'?: string
  checked?: CheckboxValue
  colorType?: 'secondary'
  'data-test-id'?: string
  disabled?: boolean
  error?: string
  id?: string
  label?: string | React.ReactNode
  name?: string
  noInput?: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  rounded?: boolean
  value?: string
} & SpaceProps

const IconWrapper = styled.span``

const CheckboxWrapper = styled(Box)<
  Pick<CheckboxProps, 'checked' | 'rounded' | 'colorType' | 'disabled'>
>`
  height: 16px;
  width: 16px;
  display: inline-flex;
  position: relative;
  border-radius: 4px;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};

  &:focus-within {
    outline: 1px solid rgba(0, 0, 0, 0.5);
  }

  ${props => {
    const borderRadius = props.rounded ? '50%' : '4px'
    return props.checked
      ? 'border: none;'
      : `box-shadow: ${props.theme.shadows['border/select-indicator-unselected']}; border-radius: ${borderRadius};`
  }}
  ${props => {
    if (!props.checked && props.disabled) {
      return `background-color: ${props.theme.colors['bg/disabled']};`
    }
  }}
  ${IconWrapper} {
    width: 100%;
    height: 100%;
    background-color: white;
    border-radius: 4px;
    display: flex;
    transition: color 0.2s;
    color: ${({ theme: { colors }, colorType, checked, disabled }) => {
      if (disabled) {
        const colorValue =
          colorType === 'secondary'
            ? 'bg/select-indicator-moderate-disabled'
            : 'bg/select-indicator-major-disabled'
        return colors[colorValue]
      }
      if (checked) {
        const colorValue =
          colorType === 'secondary' ? 'bg/select-indicator-moderate' : 'bg/select-indicator-major'
        return colors[colorValue]
      }
      return '#ccc'
    }};

    ${({ checked }) => (checked ? 'border: none;' : '')}
    ${({ disabled }) => (disabled ? 'pointer-events: none;' : '')}
  }

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
`

const Container = styled(Flex)`
  transition: background 0.2s;

  :hover {
    ${props => {
      return `background: ${props.theme.colors['bg/list-item-hover']};`
    }}
  }
`

/** @deprecated Use Checkbox from @chilipiper/design-system/src/new instead */
export const Checkbox: FC<CheckboxProps> = ({
  onChange,
  id,
  name,
  checked,
  disabled,
  rounded,
  label,
  h,
  px,
  w,
  colorType,
  noInput,
  'aria-label': ariaLabel,
  'data-test-id': testId,
  ...props
}) => {
  const CheckboxIcon = rounded
    ? SvgCheckboxRounded
    : checked === ('-' as CheckboxValue)
    ? SvgCheckboxSemi
    : SvgCheckboxChecked
  const style = { cursor: disabled ? undefined : 'pointer' }
  return (
    <Container
      {...props}
      w={w}
      as='label'
      data-test-id={testId ? `${testId}-label` : undefined}
      px={px}
      htmlFor={id}
      style={style}
      alignItems='center'
      h={h}
      bg='transparent'
    >
      <CheckboxWrapper {...{ checked, rounded, colorType, disabled }}>
        {!noInput && (
          <input
            type='checkbox'
            checked={!!checked}
            data-test-input
            onChange={onChange}
            name={name}
            id={id}
            data-test-id={testId}
            disabled={disabled}
            data-test-checked={checked}
            aria-label={ariaLabel}
          />
        )}
        {checked && (
          <IconWrapper>
            <Icon icon={CheckboxIcon} pointer={!disabled} />
          </IconWrapper>
        )}
      </CheckboxWrapper>

      {label && (
        <Box ml={2}>
          <Text width='100%'>{label}</Text>
        </Box>
      )}
    </Container>
  )
}

type FinalCheckboxFieldProps = Omit<CheckboxProps, 'name' | 'onChange'> & { name: string }

/** @deprecated Use Checkbox from @chilipiper/design-system/src/new instead */
export const FinalCheckboxField: FC<FinalCheckboxFieldProps> = ({
  name,
  value,
  ...props
}: FinalCheckboxFieldProps) => {
  return (
    <FinalField name={name} value={value} type='checkbox'>
      {({ input, meta }) => <Checkbox {...props} {...input} error={meta.touched && meta.error} />}
    </FinalField>
  )
}
